/**
 * Studio Bridge API — unified window.__cogo_studio__ layer
 *
 * Mirrors the Cogo App Bridge API (window.__cogo_tools__) pattern,
 * but targets the Studio IDE itself instead of a deployed Cogo app.
 *
 * Two domains of WebMCP Bridge:
 *   window.__cogo_tools__   → Controls deployed Coogo JSON apps (CogoRuntime)
 *   window.__cogo_studio__  → Controls Cogo Studio IDE (Zustand stores)
 *
 * External agents (LLM, automation, Claude Code) can discover and call
 * Studio tools via the same WebMCP protocol.
 */

import { useStudioStore } from '@/store/studio-store'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { mcpBus } from '@/mcp/studio-mcp-bus'
import { STUDIO_NS } from '@/mcp/types'
import {
  requiredFieldsForTool,
  type ManifestToolEntry,
} from './studio-bridge-required-fields'
import { initUndo } from '@/mcp/init-undo'
import { createValidationMiddleware } from '@/mcp/validation-middleware'
import { runtimeBridge } from '@/mcp/runtime-bridge'
import type { CliAction } from '@/store/chat-types'
import type { WorkspaceMode } from '@/store/types'
import { getCurrentThreadId } from '@/store/selectors'
import { useToastStore } from '@/store/toast-store'
import {
  isShaclViolationError,
  ShaclViolationError,
} from '@/features/design-system/ontology/shacl-error'
import { showShaclErrorToast } from '@/features/design-system/ontology/shacl-error-toast'
import { initMutationClient, subscribeAndRetry } from '@/features/design-system/ontology/mutation-client-init'
import type { ChatMessage } from '@/features/channel-router/chat-message'
import { surfaceStudioCapabilityGatewayResult } from '@/features/chat/services/studio-capability-result-surface'
import { createProductionStudioApplicationArtifactPersistenceServerSeam } from '@/features/application-ontology/application-artifact-persistence-server-seam'
import {
  clearStudioApplicationArtifactPersistenceSeam,
  installStudioApplicationArtifactPersistenceSeam,
  resolveStudioApplicationArtifactPersistencePort,
} from '@/mcp/studio-tool-registration-options'

// ─── Types ────────────────────────────────────────────────────

interface StudioToolEntry {
  name: string
  description: string
  category: 'element' | 'flow' | 'data' | 'navigation' | 'project' | 'theme' | 'generation' | 'query'
  inputSchema: Record<string, unknown>
}

interface StudioContext {
  mode: WorkspaceMode
  projectId: string | null
  projectName: string | null
  activePageId: string | null
  selectedElementId: string | null
  selectedFlowId: string | null
  story: {
    name: string
    lifecycle: string
    pageCount: number
    bddCount: number
  } | null
  blocks: string[]
  flowCount: number
  variableCount: number
  datasourceCount: number
  agenticUxTransaction: {
    id: string
    status: string
    phase: string
    projectId: string | null
    route: string | null
    reference: string | null
    lastSpec: string | null
    error: string | null
  } | null
}

interface DispatchResult {
  success: boolean
  error?: string
}

interface QueryResult {
  success: boolean
  data?: unknown
  error?: string
}

export interface CogoStudioGlobal {
  // ── Discovery ──
  getManifest(): StudioToolEntry[]
  getContext(): StudioContext
  /** Phase 4 IA-3 — derived chat thread id (account-global vs ide-<projectId>). */
  getCurrentThreadId(): string

  // ── Command Dispatch (unified) ──
  dispatch(action: CliAction): DispatchResult
  dispatchAll(actions: CliAction[]): DispatchResult[]

  // ── Workspace Navigation ──
  setMode(mode: WorkspaceMode): void
  navigate(target: { mode?: WorkspaceMode; pageId?: string; flowId?: string; section?: string }): void

  // ── Element Operations ──
  addElement(type: string, props?: Record<string, unknown>): DispatchResult
  updateElement(elementId: string, changes: Record<string, unknown>): DispatchResult
  removeElement(elementId: string): DispatchResult

  // ── Flow Operations ──
  addFlow(name: string, trigger?: string): DispatchResult
  addNode(actionType: string, label?: string): DispatchResult

  // ── Data Operations ──
  addVariable(blockName: string, name: string, type?: string, defaultValue?: unknown): DispatchResult
  addDatasource(blockName: string, name: string, config: Record<string, unknown>): DispatchResult
  addEventBinding(blockName: string, eventKey: string, flow: string): DispatchResult

  // ── Project Operations ──
  openProject(id: string): Promise<DispatchResult>
  closeProject(): void

  // ── Theme ──
  setTheme(presetId: string): DispatchResult

  // ── Generation ──
  generateBlock(spec: { name: string; type: string; description: string }): DispatchResult
  validateProject(): DispatchResult

  // ── Query (read-only) ──
  getProject(): QueryResult
  getBlocks(): QueryResult
  getFlows(): QueryResult
  getVariables(): QueryResult
  getDatasources(): QueryResult
  getElements(blockName: string): QueryResult
  getStory(): QueryResult

  // ── Runtime ──
  runtimeExecute(tool: string, params: Record<string, unknown>): Promise<{ success: boolean; data?: unknown; error?: string }>

  // ── State ──
  readonly ready: boolean
  readonly readiness: 'loading' | 'ready' | 'failed'
  readonly initializationError: string | null
  whenReady(): Promise<void>
}

// ─── Global Declaration ───────────────────────────────────────

declare global {
  interface Window {
    __cogo_studio__?: CogoStudioGlobal
  }
}

// ─── Tool Manifest ────────────────────────────────────────────

const STUDIO_TOOLS: StudioToolEntry[] = [
  // Element
  { name: 'addElement', category: 'element', description: 'Add a UI element to the active page', inputSchema: { type: { type: 'string' }, props: { type: 'object' } } },
  { name: 'updateElement', category: 'element', description: 'Update element properties', inputSchema: { elementId: { type: 'string' }, changes: { type: 'object' } } },
  { name: 'removeElement', category: 'element', description: 'Remove an element', inputSchema: { elementId: { type: 'string' } } },
  // Flow
  { name: 'addFlow', category: 'flow', description: 'Create a new ActionFlow', inputSchema: { name: { type: 'string' }, trigger: { type: 'string' } } },
  { name: 'addNode', category: 'flow', description: 'Add a node to the active flow', inputSchema: { actionType: { type: 'string' }, label: { type: 'string' } } },
  // Data
  { name: 'addVariable', category: 'data', description: 'Add a state variable', inputSchema: { blockName: { type: 'string' }, name: { type: 'string' }, type: { type: 'string' } } },
  { name: 'addDatasource', category: 'data', description: 'Add a data source', inputSchema: { blockName: { type: 'string' }, name: { type: 'string' }, config: { type: 'object' } } },
  { name: 'addEventBinding', category: 'data', description: 'Bind an event to a flow', inputSchema: { blockName: { type: 'string' }, eventKey: { type: 'string' }, flow: { type: 'string' } } },
  // Navigation
  { name: 'setMode', category: 'navigation', description: 'Switch workspace mode', inputSchema: { mode: { type: 'string', enum: ['chat', 'page', 'flow', 'data', 'preview'] } } },
  { name: 'navigate', category: 'navigation', description: 'Navigate to a specific target', inputSchema: { mode: { type: 'string' }, pageId: { type: 'string' }, flowId: { type: 'string' } } },
  { name: 'start_golden_journey', category: 'navigation', description: 'Start a chat-controlled Studio golden journey', inputSchema: { journeyId: { type: 'string' } } },
  { name: 'continue_golden_journey', category: 'navigation', description: 'Focus the next Studio golden journey step', inputSchema: {} },
  { name: 'inspect_golden_journey', category: 'query', description: 'Inspect Studio golden journey progress', inputSchema: {} },
  { name: 'clear_golden_journey', category: 'navigation', description: 'Clear the active Studio golden journey guide', inputSchema: {} },
  // Project
  { name: 'openProject', category: 'project', description: 'Open a project by ID', inputSchema: { id: { type: 'string' } } },
  { name: 'closeProject', category: 'project', description: 'Close the current project', inputSchema: {} },
  // Theme
  { name: 'setTheme', category: 'theme', description: 'Apply a theme preset', inputSchema: { presetId: { type: 'string' } } },
  // Generation
  { name: 'generateBlock', category: 'generation', description: 'Generate a block via AI', inputSchema: { name: { type: 'string' }, type: { type: 'string' }, description: { type: 'string' } } },
  { name: 'validateProject', category: 'generation', description: 'Validate all blocks in the project', inputSchema: {} },
  // Query
  { name: 'getContext', category: 'query', description: 'Get current IDE context', inputSchema: {} },
  { name: 'getProject', category: 'query', description: 'Get current project data', inputSchema: {} },
  { name: 'getBlocks', category: 'query', description: 'Get all blocks in the project', inputSchema: {} },
  { name: 'getFlows', category: 'query', description: 'Get all flows in the project', inputSchema: {} },
  { name: 'getVariables', category: 'query', description: 'Get all variables', inputSchema: {} },
  { name: 'getElements', category: 'query', description: 'Get elements in a block', inputSchema: { blockName: { type: 'string' } } },
  { name: 'getStory', category: 'query', description: 'Get the story model', inputSchema: {} },
]

type StudioToolRuntimeReadiness = 'loading' | 'ready' | 'failed'

let toolRuntimeReadiness: StudioToolRuntimeReadiness = 'loading'
let toolRuntimeInitialization: Promise<void> | null = null
let toolRuntimeInitializationError: string | null = null
let middlewareInstalled = false
const queuedToolActions: CliAction[] = []

// ─── Helpers ──────────────────────────────────────────────────

/**
 * Subset of mcpBus tool short-names that are "mutation" surfaces (REQ-BASE-01
 * scope). These are the only tools whose async outcome is surfaced through
 * the Phase-029 ChatMessage cards. Other tools (e.g. setMode, navigate,
 * query-only) are dispatched fire-and-forget without an outcome card —
 * they are sync UI state mutations whose result is already visible.
 *
 * Subset of {'add_block','modify_action','preview_changes','merge_branch'}
 * the chat-message.ts kinds permit (see channel-router/chat-message.ts:44-64);
 * we map the studio short-name to the closest cogo.ontology.* tool name for
 * the card's `tool` field. Anything not in this map gets dispatched but
 * does NOT push a ChatMessage card (avoids spamming the chat rail with
 * setMode/navigate noise).
 */
const MUTATION_OUTCOME_TOOL_MAP: Record<string, 'add_block' | 'modify_action' | 'preview_changes' | 'merge_branch'> = {
  addElement: 'add_block',
  updateElement: 'modify_action',
  removeElement: 'modify_action',
  addFlow: 'add_block',
  addNode: 'modify_action',
  addConnection: 'modify_action',
  addVariable: 'modify_action',
  addDatasource: 'modify_action',
  addEventBinding: 'modify_action',
  setTheme: 'modify_action',
  generateBlock: 'add_block',
}

/**
 * Required-fields registry for sync structural pre-validation. Keyed by the
 * tool short-name; lists the field names that MUST be present (non-null,
 * non-undefined, and for strings — non-empty) in the action.payload before
 * the dispatch is allowed to fire. Mirrors the STUDIO_TOOLS inputSchema's
 * "required" semantics without round-tripping through JSON Schema.
 *
 * Tools not listed here have no structural pre-gate (only the unknown-tool
 * gate applies); the async handler / validation-middleware enforces deeper
 * semantic validation (e.g. entity-existence) inside the async chain.
 */
/**
 * DOCUMENTED EXCEPTIONS to each tool's own declaration — no longer the source.
 *
 * Enforcement now derives from the manifest (requiredFieldsForTool). This map
 * remains only for tools whose handler legitimately supplies what the schema
 * asks for, so relaxing a requirement stays deliberate and visible rather than
 * being the default for everything not listed. Measured on the appliance
 * before this change: 88 tools declared `required`, 8 were enforced, 80 were
 * not — and the party reading that manifest is an agent.
 */
const REQUIRED_PAYLOAD_OVERRIDES: Record<string, readonly string[]> = {
  // Element CRUD — addElement aliases elementType, so we accept either key
  addElement: [], // payload may be empty; defaults applied by handler (type='button')
  updateElement: ['elementId', 'changes'],
  removeElement: ['elementId'],
  // Flow
  addFlow: ['name'],
  addNode: ['actionType'],
  addConnection: ['sourceId', 'targetId'],
  // Data
  addVariable: ['blockName', 'name'],
  addDatasource: ['blockName', 'name'],
  addEventBinding: ['blockName', 'eventKey', 'flow'],
  // Navigation
  setMode: ['mode'],
  // Project
  openProject: ['id'],
  'capability.executeOperation': ['operationId'],
  // Theme
  setTheme: ['presetId'],
}

/**
 * Sync structural pre-validation. Returns null when payload is acceptable;
 * returns a {success:false,error:reason} envelope when the payload is missing
 * required fields. Shape matches src/mcp/validation-middleware.ts:82-86 so
 * the bridge surface is uniform across sync pre-gate and async validation.
 */
function validatePayloadShape(toolShortName: string, payload: Record<string, unknown>): DispatchResult | null {
  const required = requiredFieldsForTool(
    toolShortName,
    mcpBus.getManifest() as ManifestToolEntry[],
    REQUIRED_PAYLOAD_OVERRIDES,
  )
  if (required.length === 0) return null
  for (const field of required) {
    const value = payload[field]
    if (value === undefined || value === null) {
      return { success: false, error: `Missing required field "${field}" for tool "${toolShortName}"` }
    }
    if (typeof value === 'string' && value.length === 0) {
      return { success: false, error: `Empty required field "${field}" for tool "${toolShortName}"` }
    }
  }
  return null
}

/** Public-tool check against the runtime manifest. */
function isKnownTool(toolShortName: string): boolean {
  const fullName = `${STUDIO_NS}${toolShortName}`
  const entry = mcpBus.getManifest().find((item) => item.name === fullName)
  const tags = (entry?.metadata?.tags as string[] | undefined) ?? []
  return Boolean(entry) && !tags.includes('internal')
}

async function initializeStudioToolRuntime(): Promise<void> {
  if (toolRuntimeReadiness === 'ready') return
  if (toolRuntimeInitialization) return toolRuntimeInitialization

  toolRuntimeInitialization = (async () => {
    try {
      const { registerStudioTools } = await import('@/mcp/register-studio-tools')
      // Generic Application Ontology Program — wiring lane A1, seam 2. The
      // artifact-persistence port comes from the ONE resolver both
      // registration sites read (this one and the generation path's
      // `ensureStudioGenerationToolsRegistered`), because `registerStudioTools`
      // registers once and either site may win the race. With no server seam
      // installed the resolver yields `{}` — today's bare call, unchanged.
      const { resolveStudioToolRegistrationOptions } = await import(
        '@/mcp/studio-tool-registration-options'
      )
      registerStudioTools(resolveStudioToolRegistrationOptions())

      initMutationClient()
      subscribeAndRetry()
      if (!middlewareInstalled) {
        initUndo()
        mcpBus.use(createValidationMiddleware())
        middlewareInstalled = true
      }

      toolRuntimeReadiness = 'ready'
      toolRuntimeInitializationError = null
      const queued = queuedToolActions.splice(0)
      for (const action of queued) safeDispatch(action)
    } catch (error) {
      toolRuntimeReadiness = 'failed'
      toolRuntimeInitializationError =
        error instanceof Error ? error.message : String(error)
      queuedToolActions.splice(0)
      useToastStore.getState().addToast({
        type: 'error',
        message: `Studio WebMCP tools failed to initialize: ${toolRuntimeInitializationError}`,
        duration: 8000,
      })
      throw error
    }
  })()

  return toolRuntimeInitialization
}

/**
 * D-04 minimal-truthful safeDispatch. Two layers:
 *
 * 1. SYNCHRONOUS pre-gate (the REQ-BASE-01 fix):
 *      - Unknown tool short-name (not in mcpBus manifest) → {success:false}
 *      - Structurally-invalid payload (missing required fields per
 *        the tool's own inputSchema.required) → {success:false}
 *      - Both rejected BEFORE mcpBus.execute is called (no async dispatch).
 *      - Envelope shape mirrors src/mcp/validation-middleware.ts:82-86.
 *
 * 2. ASYNC outcome surfacing (the REQ-BASE-01 "UI-tellable" half):
 *      - On resolve → useStudioStore.appendChatMessage({kind:'mutation-applied'})
 *      - On reject  → useStudioStore.appendChatMessage({kind:'mutation-failed'})
 *                     AND a toast: SHACL via showShaclErrorToast (reuses
 *                     Phase-029 -32001 path), other via useToastStore
 *                     plain error toast.
 *      - Only tools in MUTATION_OUTCOME_TOOL_MAP push cards (avoids spam
 *        from setMode/navigate); other tools dispatch silently as before.
 *
 * Sync `DispatchResult` return type preserved (D-04 hard constraint —
 * chat-model-adapter.ts uses mcpBus.execute directly, not safeDispatch,
 * so the sync contract on window.__cogo_studio__ stays unbroken).
 * "return {success:true}" now means "dispatch accepted" — persistence
 * truth is tellable from the chat card / toast.
 */
function safeDispatch(action: CliAction): DispatchResult {
  // ── (1) Synchronous pre-gate ──
  try {
    if (!isKnownTool(action.type)) {
      return { success: false, error: `Unknown tool "${action.type}"` }
    }
    const payloadInvalid = validatePayloadShape(action.type, action.payload ?? {})
    if (payloadInvalid) return payloadInvalid
  } catch (err) {
    // A sync pre-gate failure (e.g. mcpBus manifest threw) is itself a
    // synchronous dispatch failure — surface it through the same envelope.
    return { success: false, error: err instanceof Error ? err.message : String(err) }
  }

  if (toolRuntimeReadiness === 'failed') {
    return {
      success: false,
      error: toolRuntimeInitializationError ?? 'Studio WebMCP tools are unavailable',
    }
  }
  if (toolRuntimeReadiness !== 'ready') {
    queuedToolActions.push(action)
    void initializeStudioToolRuntime().catch(() => {
      // Initialization failure is exposed through bridge state and toast.
    })
    return { success: true }
  }

  // ── (2) Fire async dispatch + surface real outcome ──
  const outcomeTool = MUTATION_OUTCOME_TOOL_MAP[action.type]
  try {
    const exec = mcpBus.executeWebMcp(
      {
        tool: `${STUDIO_NS}${action.type}`,
        params: action.payload,
      },
      // Wave 4 lane 1 — this site pushes its OWN 'mutation-applied'/
      // 'mutation-failed' card below (pushMutationApplied/pushMutationFailed)
      // for exactly the tools `outcomeTool` names; the bus's central
      // disclosure emitter must not ALSO disclose those, or every one of
      // the 11 MUTATION_OUTCOME_TOOL_MAP tools would show two cards for one
      // write. Every other tool dispatched through this same bridge (the
      // ~171 tools NOT in that map) has no site-level disclosure at all and
      // must still get the central one — hence keying the flag off the same
      // `outcomeTool` truthiness this function already computes, not a
      // second, separately maintained list.
      { siteHandlesDisclosure: Boolean(outcomeTool) },
    )
    if (action.type === 'capability.executeOperation') {
      exec
        .then((result) => {
          const surfaced = surfaceStudioCapabilityGatewayResult(result)
          if (!surfaced.success) {
            useToastStore.getState().addToast({
              type: 'error',
              message: surfaced.error ?? 'Studio capability execution failed.',
              duration: 8000,
            })
          }
        })
        .catch((err) => {
          useToastStore.getState().addToast({
            type: 'error',
            message: `Studio capability execution failed: ${
              err instanceof Error ? err.message : String(err)
            }`,
            duration: 8000,
          })
        })
    } else if (outcomeTool) {
      exec
        .then((result) => {
          if (result.success) {
            pushMutationApplied(action.type, outcomeTool, result.data)
          } else {
            // mcpBus returns {success:false,error:...} for tool-level / middleware
            // rejections (e.g. validation-middleware unknown-entity); treat as fail.
            pushMutationFailed(outcomeTool, new Error(result.error ?? 'Unknown dispatch failure'))
          }
        })
        .catch((err) => {
          pushMutationFailed(outcomeTool, err)
        })
    } else {
      // Non-mutation tools (setMode/navigate/query) — no chat card, but still
      // log async errors so they're not silently swallowed (legacy behavior).
      exec.catch((err) => console.error('[studio-bridge] async dispatch error:', err))
    }
    return { success: true }
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : String(err) }
  }
}

/**
 * Push a 'mutation-applied' ChatMessage when an async dispatch resolves
 * successfully. Best-effort field extraction from the unknown handler
 * payload — when the handler does not return structured AddBlockResult /
 * ModifyActionResult shape (local-only mutation today, REQ-BASE-02/03 wiring
 * tracked in Plan 03), defaults to insertedCount:0 + empty subjects so the
 * card still renders.
 */
function pushMutationApplied(
  shortName: string,
  tool: 'add_block' | 'modify_action' | 'preview_changes' | 'merge_branch',
  data: unknown,
): void {
  // mutation-applied union member only permits these three tool values;
  // collapse preview_changes onto modify_action so the type stays sound.
  const cardTool: 'add_block' | 'modify_action' | 'merge_branch' =
    tool === 'preview_changes' ? 'modify_action' : tool
  const insertedCount = extractNumber(data, 'insertedCount') ?? 0
  const subjects = extractStringArray(data, 'subjects') ?? []
  const msg: ChatMessage = {
    kind: 'mutation-applied',
    id: makeMsgId(shortName),
    timestamp: Date.now(),
    tool: cardTool,
    insertedCount,
    subjects,
    auditSource: 'webmcp',
  }
  useStudioStore.getState().appendChatMessage(msg)
}

/**
 * Push a 'mutation-failed' ChatMessage AND a toast. SHACL `-32001` violations
 * (D-04 spec) flow through showShaclErrorToast (reuses the Phase 029 SHACL
 * path); other failures (transport / generic) use a plain error toast.
 */
function pushMutationFailed(
  tool: 'add_block' | 'modify_action' | 'preview_changes' | 'merge_branch',
  err: unknown,
): void {
  let summary: string
  let violationCount = 0
  if (isShaclViolationError(err)) {
    const shacl: ShaclViolationError = err
    violationCount = shacl.violations.length
    summary = shacl.message
    showShaclErrorToast(shacl)
  } else {
    summary = err instanceof Error ? err.message : String(err)
    useToastStore.getState().addToast({
      type: 'error',
      message: `Dispatch failed: ${summary}`,
      duration: 8000,
    })
  }
  const msg: ChatMessage = {
    kind: 'mutation-failed',
    id: makeMsgId(tool),
    timestamp: Date.now(),
    tool,
    summary,
    violationCount,
  }
  useStudioStore.getState().appendChatMessage(msg)
}

let _msgCounter = 0
function makeMsgId(scope: string): string {
  _msgCounter += 1
  return `bridge-${scope}-${Date.now()}-${_msgCounter}`
}

function extractNumber(data: unknown, key: string): number | undefined {
  if (!data || typeof data !== 'object') return undefined
  const v = (data as Record<string, unknown>)[key]
  return typeof v === 'number' ? v : undefined
}

function extractStringArray(data: unknown, key: string): string[] | undefined {
  if (!data || typeof data !== 'object') return undefined
  const v = (data as Record<string, unknown>)[key]
  if (!Array.isArray(v)) return undefined
  return v.filter((x): x is string => typeof x === 'string')
}

function collectElements(node: any): any[] {
  const result = [{ id: node.id, type: node.type, text: node.text, className: node.className }]
  if (node.children) {
    for (const child of node.children) {
      result.push(...collectElements(child))
    }
  }
  return result
}

// ─── Install ──────────────────────────────────────────────────

let bridgeInstalledArtifactPersistenceSeam = false

export function installStudioBridge(): void {
  if (typeof window === 'undefined') return

  // Lane C2b Stage 4 — install the server-backed artifact-persistence seam
  // HERE, synchronously at the production bootstrap, BEFORE either racing
  // registration site (`initializeStudioToolRuntime` below, or the
  // generation path's `ensureStudioGenerationToolsRegistered`) can call
  // `registerStudioTools()` through the A1 options resolver. First install
  // wins: a seam already installed (a test's, or a future host's) is not
  // clobbered.
  if (!resolveStudioApplicationArtifactPersistencePort()) {
    installStudioApplicationArtifactPersistenceSeam(
      createProductionStudioApplicationArtifactPersistenceServerSeam(),
    )
    bridgeInstalledArtifactPersistenceSeam = true
  }

  const bridge: CogoStudioGlobal = {
    get ready() {
      return toolRuntimeReadiness === 'ready'
    },

    get readiness() {
      return toolRuntimeReadiness
    },

    get initializationError() {
      return toolRuntimeInitializationError
    },

    whenReady() {
      return initializeStudioToolRuntime()
    },

    // ── Discovery ──

    getManifest() {
      // Delegate to mcpBus manifest (includes all registered MCP tools)
      // Fall back to static STUDIO_TOOLS for backward compat shape
      const mcpManifest = mcpBus.getManifest().filter((entry) => {
        const tags = (entry.metadata?.tags as string[] | undefined) ?? []
        return !tags.includes('internal')
      })
      if (mcpManifest.length > 0) {
        return mcpManifest.map((entry) => ({
          name: entry.name.replace(STUDIO_NS, ''),
          description: entry.description,
          category: 'element' as const,
          inputSchema: entry.inputSchema as Record<string, unknown>,
        }))
      }
      return STUDIO_TOOLS
    },

    getCurrentThreadId(): string {
      return getCurrentThreadId(useStudioStore.getState())
    },

    getContext(): StudioContext {
      const s = useStudioStore.getState()
      const e = useEditorStore.getState()
      const f = useFlowEditorStore.getState()

      return {
        mode: s.mode,
        projectId: s.currentProjectId,
        projectName: s.currentProject?.manifest?.name ?? null,
        activePageId: e.activePageId,
        selectedElementId: e.selectedIds?.[0] ?? null,
        selectedFlowId: f.selectedFlowId ?? null,
        story: s.story ? {
          name: s.story.name,
          lifecycle: s.story.lifecycle,
          pageCount: s.story.pages.length,
          bddCount: s.story.bddScenarios.length,
        } : null,
        blocks: Object.keys(s.currentProject?.blocks ?? {}),
        flowCount: countProjectFlows(s.currentProject, f.flows.length),
        variableCount: countProjectVariables(s.currentProject),
        datasourceCount: countProjectDatasources(s.currentProject),
        agenticUxTransaction: s.agenticUxTransaction ? {
          id: s.agenticUxTransaction.id,
          status: s.agenticUxTransaction.status,
          phase: s.agenticUxTransaction.phase,
          projectId: s.agenticUxTransaction.projectId,
          route: s.agenticUxTransaction.route,
          reference: s.agenticUxTransaction.reference,
          lastSpec: s.agenticUxTransaction.lastSpec,
          error: s.agenticUxTransaction.error,
        } : null,
      }
    },

    // ── Command Dispatch ──

    dispatch(action: CliAction): DispatchResult {
      return safeDispatch(action)
    },

    dispatchAll(actions: CliAction[]): DispatchResult[] {
      return actions.map((a) => safeDispatch(a))
    },

    // ── Workspace Navigation ──

    setMode(mode: WorkspaceMode) {
      useStudioStore.getState().setMode(mode)
    },

    navigate(target) {
      if (target.mode) useStudioStore.getState().setMode(target.mode)
      if (target.pageId) useEditorStore.getState().setActivePage(target.pageId)
      if (target.flowId) {
        const flows = useFlowEditorStore.getState().flows
        const flow = flows.find((f) => f.id === target.flowId || f.name === target.flowId)
        if (flow) useFlowEditorStore.getState().selectFlow(flow.id)
      }
      if (target.section) {
        useStudioStore.getState().setScrollTarget(target.section as any)
      }
    },

    // ── Element Operations ──

    addElement(type: string, props?: Record<string, unknown>): DispatchResult {
      return safeDispatch({
        type: 'addElement',
        payload: { elementType: type, properties: props ?? {} },
      })
    },

    updateElement(elementId: string, changes: Record<string, unknown>): DispatchResult {
      return safeDispatch({
        type: 'updateElement',
        payload: { elementId, changes },
      })
    },

    removeElement(elementId: string): DispatchResult {
      return safeDispatch({
        type: 'removeElement',
        payload: { elementId },
      })
    },

    // ── Flow Operations ──

    addFlow(name: string, trigger = 'event'): DispatchResult {
      return safeDispatch({
        type: 'addFlow',
        payload: { name, trigger },
      })
    },

    addNode(actionType: string, label?: string): DispatchResult {
      return safeDispatch({
        type: 'addNode',
        payload: { actionType, label },
      })
    },

    // ── Data Operations ──

    addVariable(blockName: string, name: string, type = 'string', defaultValue?: unknown): DispatchResult {
      return safeDispatch({
        type: 'addVariable',
        payload: { blockName, name, type, defaultValue },
      })
    },

    addDatasource(blockName: string, name: string, config: Record<string, unknown>): DispatchResult {
      return safeDispatch({
        type: 'addDatasource',
        payload: { blockName, name, ...config },
      })
    },

    addEventBinding(blockName: string, eventKey: string, flow: string): DispatchResult {
      return safeDispatch({
        type: 'addEventBinding',
        payload: { blockName, eventKey, flow },
      })
    },

    // ── Project Operations ──

    async openProject(id: string): Promise<DispatchResult> {
      try {
        await useStudioStore.getState().openProject(id)
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : String(err) }
      }

      // Resolving is not opening. The store reports an unresolvable id through
      // `projectLoadError` and leaves the workspace as it was, so returning
      // success on "did not throw" answers a question nobody asked — measured
      // in a browser: this returned `{ success: true }` while
      // `currentProjectId` stayed null, and three later diagnoses were spent
      // blaming the steps after it.
      //
      // The condition is the one the MCP tool of the same name already
      // requires before it reports (`register-studio-tools.ts`), rather than a
      // second opinion invented here.
      const state = useStudioStore.getState()
      const opened =
        state.currentProjectId === id &&
        Boolean(state.currentProject) &&
        !state.projectLoadError
      if (opened) return { success: true }

      return {
        success: false,
        error:
          state.projectLoadError ??
          `openProject: project "${id}" did not open`,
      }
    },

    closeProject() {
      useStudioStore.getState().closeProject()
    },

    // ── Theme ──

    setTheme(presetId: string): DispatchResult {
      return safeDispatch({
        type: 'setThemePreset',
        payload: { presetId },
      })
    },

    // ── Generation ──

    generateBlock(spec): DispatchResult {
      return safeDispatch({
        type: 'requestBlockGeneration',
        payload: spec,
      })
    },

    validateProject(): DispatchResult {
      return safeDispatch({
        type: 'requestValidation',
        payload: {},
      })
    },

    // ── Query (read-only) ──

    getProject(): QueryResult {
      const p = useStudioStore.getState().currentProject
      if (!p) return { success: false, error: 'No project loaded' }
      return {
        success: true,
        data: {
          id: useStudioStore.getState().currentProjectId,
          name: p.manifest?.name,
          pages: p.manifest?.pages?.map((pg: any) => ({
            id: pg.id,
            route: pg.route,
            title: pg.title,
            blocks: pg.blocks,
          })),
          blockCount: Object.keys(p.blocks ?? {}).length,
          flowCount: Object.keys(p.flows ?? {}).length,
        },
      }
    },

    getBlocks(): QueryResult {
      const p = useStudioStore.getState().currentProject
      if (!p) return { success: false, error: 'No project loaded' }
      const blocks: Record<string, unknown> = {}
      for (const [name, def] of Object.entries(p.blocks ?? {})) {
        blocks[name] = {
          schemaVersion: (def as any).schemaVersion,
          blockType: (def as any).blockType,
          elementCount: countElements(getBlockRoot(def)),
        }
      }
      return { success: true, data: blocks }
    },

    getFlows(): QueryResult {
      const p = useStudioStore.getState().currentProject
      if (!p?.flows) return { success: false, error: 'No flows' }
      const flows: Record<string, unknown> = {}
      for (const [name, flow] of Object.entries(p.flows)) {
        flows[name] = {
          trigger: (flow as any).trigger,
          stepCount: (flow as any).steps?.length ?? 0,
        }
      }
      return { success: true, data: flows }
    },

    getVariables(): QueryResult {
      const p = useStudioStore.getState().currentProject
      if (!p) return { success: false, error: 'No project loaded' }
      const vars: Record<string, unknown> = {}
      for (const [blockName, block] of Object.entries(p.blocks ?? {})) {
        const blockVars = (block as any).state ?? (block as any).variables
        if (blockVars && typeof blockVars === 'object') {
          vars[blockName] = blockVars
        }
      }
      return { success: true, data: vars }
    },

    getDatasources(): QueryResult {
      const p = useStudioStore.getState().currentProject
      if (!p) return { success: false, error: 'No project loaded' }
      const datasources: Record<string, unknown> = {}
      for (const [blockName, block] of Object.entries(p.blocks ?? {})) {
        const blockData = (block as any).data ?? (block as any).datasources
        if (blockData && typeof blockData === 'object') {
          datasources[blockName] = blockData
        }
      }
      return { success: true, data: datasources }
    },

    getElements(blockName: string): QueryResult {
      const p = useStudioStore.getState().currentProject
      if (!p) return { success: false, error: 'No project loaded' }
      const block = p.blocks?.[blockName]
      if (!block) return { success: false, error: `Block "${blockName}" not found` }
      return { success: true, data: collectElements(getBlockRoot(block)) }
    },

    getStory(): QueryResult {
      const story = useStudioStore.getState().story
      if (!story) return { success: false, error: 'No story' }
      return { success: true, data: story }
    },

    // ── Runtime ──

    async runtimeExecute(tool: string, params: Record<string, unknown>) {
      await initializeStudioToolRuntime()
      return runtimeBridge.execute({ tool, params })
    },
  }

  window.__cogo_studio__ = bridge
  // Expose runtime bridge for external access (DevTools, automation)
  ;(window as any).__cogo_runtime_bridge__ = runtimeBridge
  void initializeStudioToolRuntime().catch((error) => {
    console.error('[studio-bridge] WebMCP initialization failed:', error)
  })
  console.log('[studio-bridge] window.__cogo_studio__ installed; WebMCP tools loading')
}

// ─── Uninstall ────────────────────────────────────────────────

export function uninstallStudioBridge(): void {
  // Clear ONLY what this bootstrap installed — a seam installed by someone
  // else (a test's, a future host's) is theirs to clear.
  if (bridgeInstalledArtifactPersistenceSeam) {
    clearStudioApplicationArtifactPersistenceSeam()
    bridgeInstalledArtifactPersistenceSeam = false
  }
  if (typeof window !== 'undefined') {
    delete window.__cogo_studio__
  }
}

// ─── Helpers ──────────────────────────────────────────────────

function countProjectVariables(project: any): number {
  if (!project?.blocks) return 0
  let count = 0
  for (const block of Object.values(project.blocks)) {
    const vars = (block as any).state ?? (block as any).variables
    if (vars && typeof vars === 'object') {
      count += Object.values(vars).reduce<number>((total, slot) => (
        total + (slot && typeof slot === 'object' ? Object.keys(slot).length : 0)
      ), 0)
    }
  }
  return count
}

function countProjectDatasources(project: any): number {
  if (!project?.blocks) return 0
  let count = 0
  for (const block of Object.values(project.blocks)) {
    const datasources = (block as any).data ?? (block as any).datasources
    if (datasources && typeof datasources === 'object') {
      count += Object.keys(datasources).length
    }
  }
  return count
}

function countProjectFlows(project: any, fallback = 0): number {
  if (project?.flows && typeof project.flows === 'object') {
    return Object.keys(project.flows).length
  }
  return fallback
}

function getBlockRoot(block: any): any {
  return block?.ui?.root ?? block?.root
}

function countElements(node: any): number {
  if (!node) return 0
  let count = 1
  if (node.children) {
    for (const child of node.children) {
      count += countElements(child)
    }
  }
  return count
}
